import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Paper,
    IconButton,
    Switch,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useForm, Controller } from "react-hook-form";

import WSSelectField from "../../../Components/Select/WSSelect";
import WSTextField, { TextType, CurrencyType } from "../../../Components/TextBox/WSTextBox";
import { callAPI, Method } from "../../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmationPopup from "../../../Components/Confirmation/Confirmation";
import { useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";



// ✅ TypeScript Interfaces
interface Category {
    CategoryId: string;
    CategoryName: string;
    Description: string;
    IsActive: boolean;
}

interface Unit {
    UnitId: string;
    UnitName: string;
    Symbol: string;
    IsActive: boolean;
}

interface Brand {
    BrandId: string;
    BrandName: string;
}

interface Supplier {
    SupplierId: string;
    SupplierName: string;
    ContactPerson: string;
    Phone: string;
    altphone: string;
    Email: string;
    Address: string;
    GSTNumber: string;
    IsActive: boolean;
}

interface Warehouse {
    WarehouseId: string;
    WarehouseName: string;
    Location: string;
    PhoneNumber: string;
    AlternatePhone: string;
    Email: string;
    GSTNumber: string;
    IsActive: boolean;
}

interface ItemData {
    ItemCode: string;
    ItemName: string;
    Brand: string;
    ModelNo: string;
    CategoryId: string;
    UnitId: string;
    SN: string;
    SerialTracking: string;
    PurchasePrice: number;
    SellingPrice: number;
    Vat: number;
    WarrantyMonths: number;
    ReorderLevel: number;
    MinStock: number;
    WarehouseId: string;
    SupplierId: string;
    Technician: string;
    IsActive: boolean;
}

const ItemMaster = () => {
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const { showMessage } = useSnackbar();
    const params = useParams();
    const id = params.id;
    const navigate = useNavigate();

    // ✅ Properly typed state
    const [categories, setCategories] = useState<Category[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [selectedId, setSelectedId] = useState<string | number>(0);
    const [isPopupOpen, setPopupOpen] = useState(false);

    const location = useLocation();
    const from = location.state?.from;


    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            ItemCode: "",
            ItemName: "",
            brand: "",
            modelNumber: "",
            category: "",
            unit: "",
            serialno: "",
            tracking: false,
            vat: 5,
            warrantymonths: "",
            purchasePrice: "",
            sellingPrice: "",
            stock: "",
            reorderlevel: "",
            warehouse: "",
            supplier: "",
            // technician: "",
            IsActive: true,
        } as any,
    });

    const fetchAllData = async (url: string) => {
        let allData: any[] = [];
        let page = 1;
        let totalPages = 1;

        try {
            do {
                const response = await callAPI(
                    `${url}?page=${page}&pageSize=50`,
                    Method.GET
                );

                const data = response?.data;

                if (data?.results) {
                    allData = [...allData, ...data.results];
                    totalPages = data.totalPages || 1;
                }

                page++;
            } while (page <= totalPages);

            return allData;

        } catch (error) {
            console.error(`Error fetching ${url}:`, error);
            return [];
        }
    };


    // Fetch dropdown data
    const fetchDropdownData = async () => {
        try {
            setLoading(true);

            const [
                allCategories,
                allUnits,
                allSuppliers,
                allWarehouses,
                allBrands
            ] = await Promise.all([
                fetchAllData("categories/list"),
                fetchAllData("units/list"),
                fetchAllData("suppliers/list"),
                fetchAllData("warehouses/list"),
                fetchAllData("brands/list"),
            ]);

            setCategories(allCategories);
            setUnits(allUnits);
            setSuppliers(allSuppliers);
            setWarehouses(allWarehouses);
            setBrands(allBrands);

        } catch (error) {
            console.error("Dropdown fetch error:", error);
            showMessage("Failed to load dropdown data", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {

        if (!id || id === "undefined" || id === "null" || id.trim() === "") {
            setIsEditMode(false);
            reset();
            fetchDropdownData();
            return;
        }

        setIsEditMode(true);
        fetchItemData(id);
        fetchDropdownData();
    }, [id, reset]);

    const fetchItemData = async (itemId: string) => {
        try {
            setLoading(true);

            const response = await callAPI(`items/${itemId}`, Method.GET);

            if (response?.success && response.data) {
                const itemData: ItemData = response.data;

                reset({
                    ItemCode: itemData.ItemCode || "",
                    ItemName: itemData.ItemName || "",
                    brand: itemData.Brand || "",
                    modelNumber: itemData.ModelNo || "",
                    category: itemData.CategoryId || "",
                    unit: itemData.UnitId || "",
                    serialno: itemData.SN || "",
                    tracking: itemData.SerialTracking || false,
                    vat: itemData.Vat?.toString() || "",
                    warrantymonths: itemData.WarrantyMonths?.toString() || "",
                    purchasePrice: itemData.PurchasePrice?.toString() || "",
                    sellingPrice: itemData.SellingPrice?.toString() || "",
                    stock: itemData.MinStock?.toString() || "",
                    reorderlevel: itemData.ReorderLevel?.toString() || "",
                    warehouse: itemData.WarehouseId || "",
                    supplier: itemData.SupplierId || "",
                    // technician: itemData.Technician || "",
                    IsActive: itemData.IsActive !== false,
                });
            } else {
                console.error("❌ No data in response:", response);
                showMessage(response?.message || `Item ${itemId} not found`, "error");
                setIsEditMode(false);
            }
        } catch (error: any) {
            console.error("❌ Fetch error:", error.response?.data || error.message);
            showMessage("Failed to load item data", "error");
            setIsEditMode(false);
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (data: any) => {
        try {
            setLoading(true);
            const requestBody: Partial<ItemData> = {
                ItemCode: data.ItemCode?.trim() || "",
                ItemName: data.ItemName?.trim() || "",
                Brand: data.brand?.trim() || "",
                ModelNo: data.modelNumber?.trim() || "",
                CategoryId: data.category || "",
                UnitId: data.unit || "",
                SN: data.serialno?.trim() || "",
                SerialTracking: (data.tracking || false).toString(),
                PurchasePrice: parseFloat(data.purchasePrice) || 0,
                SellingPrice: parseFloat(data.sellingPrice) || 0,
                Vat: parseFloat(data.vat) || 0,
                WarrantyMonths: parseInt(data.warrantymonths) || 0,
                ReorderLevel: parseInt(data.reorderlevel) || 0,
                MinStock: parseInt(data.stock) || 0,
                WarehouseId: data.warehouse || "",
                SupplierId: data.supplier || "",
                // Technician: data.technician?.trim() || "",
                IsActive: data.IsActive !== false,
            };


            console.log('data ',requestBody)
            let response;
            if (isEditMode && id) {
                response = await callAPI(`items/${id}`, Method.PATCH, requestBody);
            } else {
                response = await callAPI("items/create", Method.POST, requestBody);
            }

            if (response?.success) {
                showMessage(
                    isEditMode ? "Item updated successfully!" : "Item created successfully!",
                    "success"
                );
                // navigate("/itemmaster/list");
                if (response?.success) {
                    if (from === "openingstock") {
                        navigate("/openingstock");
                    } else if (from === "adjustment") {
                        navigate("/adjustment");
                    }
                    else if (from === "verification") {
                        navigate("/verification");
                    }
                    else if (from === "stock-return") {
                        navigate("/stock-return");
                    }
                    else if (from === "stockin") {
                        navigate("/stockin");
                    }
                    else if (from === "stockout") {
                        navigate("/stockout");
                    }

                    else {
                        navigate("/itemmaster/list");
                    }
                }
            } else {
                showMessage(response?.message || "Operation failed", "error");
            }
        } catch (error: any) {
            console.error("❌ Submit error:", error);
            showMessage(error?.message || "Operation failed", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = () => {
        if (!id) return;
        setSelectedId(id);
        setPopupOpen(true);
    };


    return (
        <Box sx={{ p: 2, background: "#f0f0f0", minHeight: "100vh" }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
                        {isEditMode ? "Edit Item" : "Item Master"}
                    </Typography>
                </Box>
                <Box>
                    {/* <IconButton 
                        sx={{ background: "#000", color: "#fff", mr: 1 }} 
                        onClick={() => setIsEditMode(!isEditMode)}
                        disabled={loading}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton> */}
                    {isEditMode && id && (
                        <IconButton
                            onClick={handleDeleteClick}
                            disabled={loading}
                            sx={{
                                background: "black",
                                color: "#fff",
                                "&:hover": { background: "black" },
                            }}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                    )}
                </Box>
            </Box>

            { }
            <Paper
                sx={{
                    borderRadius: "16px",
                    p: 3,
                    background: "#fafafa",
                    position: "relative"
                }}
            >
                {loading && (
                    <Box sx={{
                        position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(255,255,255,0.9)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 10, borderRadius: "16px"
                    }}>
                        <CircularProgress />
                    </Box>
                )}

                { }
                <Box display="flex" justifyContent="space-between" mb={7}>
                    <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
                        {isEditMode ? "Item Details" : "Item Details"}
                    </Typography>
                    <Tooltip title={isEditMode ? "Update Item" : "Create Item"} arrow>
                        <IconButton
                            sx={{ background: "#000", color: "#fff" }}
                            onClick={handleSubmit(onSubmit)}
                            disabled={!isValid || loading}
                        >
                            <SaveIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Grid container rowSpacing={8} columnSpacing={5}>
                    { }
                    <Grid item xs={3}>
                        <WSTextField
                            name="ItemCode"
                            label="Item Code"
                            id="ItemCode"
                            control={control}
                        />
                    </Grid>


                    <Grid item xs={9}>
                        <WSTextField
                            name="ItemName"
                            label="Item Name"
                            id="ItemName"
                            control={control}
                        />
                    </Grid>

                    { }
                    <Grid item xs={3}>
                        <WSSelectField
                            name="brand"
                            label="Brand"
                            id="brand"
                            control={control}
                            inlineSearch
                            options={brands.map(b => ({
                                value: b.BrandId,
                                label: b.BrandName
                            }))}
                        />
                    </Grid>


                    <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
                        <Box
                            onClick={() =>
                                navigate("/itemmaster/brand", { state: { from: "itemmaster" } })
                            }
                            sx={{
                                height: "36px",
                                width: "36px",
                                borderRadius: "10px",
                                background: "#f3f3f3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                "&:hover": {
                                    background: "#e0e0e0",
                                },
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </Box>
                    </Grid>

                    <Grid item xs={3}>
                        <WSTextField
                            name="modelNumber"
                            label="Model Number"
                            id="modelNumber"
                            control={control}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <WSSelectField
                            name="category"
                            label="Category"
                            id="category"
                            control={control}
                            inlineSearch
                            options={categories.map(cat => ({
                                value: cat.CategoryId,
                                label: cat.CategoryName
                            }))}
                        />
                    </Grid>

                    <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
                        <Box
                            onClick={() =>
                                navigate("/itemmaster/category", { state: { from: "category" } })
                            }
                            sx={{
                                height: "36px",
                                width: "36px",
                                borderRadius: "10px",
                                background: "#f3f3f3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                "&:hover": {
                                    background: "#e0e0e0",
                                },
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </Box>
                    </Grid>



                    <Grid item xs={3}>
                        <WSSelectField
                            name="unit"
                            label="Unit"
                            id="unit"
                            control={control}
                            options={units.map(unit => ({
                                value: unit.UnitId,
                                label: unit.UnitName
                            }))}
                            inlineSearch
                        />
                    </Grid>

                    <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
                        <Box
                            onClick={() =>
                                navigate("/itemmaster/unit", { state: { from: "unit" } })
                            }
                            sx={{
                                height: "36px",
                                width: "36px",
                                borderRadius: "10px",
                                background: "#f3f3f3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                "&:hover": {
                                    background: "#e0e0e0",
                                },
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </Box>
                    </Grid>

                    { }
                    <Grid item xs={3}>
                        <WSTextField
                            name="serialno"
                            label="Serial No"
                            id="serialno"
                            control={control}
                        />
                    </Grid>

                    {/* <Grid item xs={3}>
                        <WSTextField
                            name="vat"
                            label="Vat"
                            id="vat"
                            control={control}
                            type={TextType.AllowOnlyNumber}
                        />
                    </Grid> */}

                    <Grid item xs={3}>
                        <WSTextField
                            name="warrantymonths"
                            label="Warranty Months"
                            id="warrantymonths"
                            control={control}
                            type={TextType.AllowOnlyNumber}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <WSTextField
                            name="purchasePrice"
                            label="Purchase Price"
                            id="purchasePrice"
                            control={control}
                            type={TextType.Currency}
                            currency={CurrencyType.Rupee}
                        />
                    </Grid>

                    { }
                    <Grid item xs={3}>
                        <WSTextField
                            name="sellingPrice"
                            label="Selling Price"
                            id="sellingPrice"
                            control={control}
                            type={TextType.Currency}
                            currency={CurrencyType.Rupee}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <WSTextField
                            name="stock"
                            label="Min Stock"
                            id="stock"
                            control={control}
                            type={TextType.AllowOnlyNumber}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <WSTextField
                            name="reorderlevel"
                            label="Reorder Level"
                            id="reorderlevel"
                            control={control}
                            type={TextType.AllowOnlyNumber}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <WSSelectField
                            name="warehouse"
                            label="Warehouse Name"
                            id="warehouse"
                            control={control}
                            options={warehouses.map(wh => ({
                                value: wh.WarehouseId,
                                label: wh.WarehouseName
                            }))}
                            inlineSearch
                        />
                    </Grid>

                    <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
                        <Box
                            onClick={() =>
                                navigate("/itemmaster/warehouse", { state: { from: "warehouse" } })
                            }
                            sx={{
                                height: "36px",
                                width: "36px",
                                borderRadius: "10px",
                                background: "#f3f3f3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                "&:hover": {
                                    background: "#e0e0e0",
                                },
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </Box>
                    </Grid>


                    { }
                    <Grid item xs={3}>
                        <WSSelectField
                            name="supplier"
                            label="Supplier Name"
                            id="supplier"
                            control={control}
                            options={suppliers.map(sup => ({
                                value: sup.SupplierId,
                                label: sup.SupplierName
                            }))}
                            inlineSearch
                        />
                    </Grid>


                    <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
                        <Box
                            onClick={() =>
                                navigate("/itemmaster/supplier", { state: { from: "warehouse" } })
                            }
                            sx={{
                                height: "36px",
                                width: "36px",
                                borderRadius: "10px",
                                background: "#f3f3f3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                flexShrink: 0,
                                border: "1px solid #d1d5db",
                                "&:hover": {
                                    background: "#e0e0e0",
                                },
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </Box>
                    </Grid>

                    {/* <Grid item xs={3}>
                        <WSTextField
                            name="technician"
                            label="Technician"
                            id="technician"
                            control={control}
                        />
                    </Grid> */}

                    <Grid item xs={3}>
                        <Typography sx={{ fontSize: "0.75rem", mb: 1 }}>
                            Status
                        </Typography>
                        { }
                        <Controller
                            name="IsActive"
                            control={control}
                            render={({ field }) => (
                                <Switch
                                    {...field}
                                    checked={field.value || false}
                                    color="success"
                                    onChange={(e) => field.onChange(e.target.checked)}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <ConfirmationPopup
                type="DELETE"
                apiUrl="items"
                id={selectedId}
                confirmMessage="Are you sure you want to delete this item?"
                open={isPopupOpen}
                onClose={() => setPopupOpen(false)}
                onConfirm={async (data) => {
                    if (!selectedId) return;

                    if (data?.success === true) {


                        showMessage("item deleted successfully!", "success");
                        navigate("/itemmaster/list");
                    } else {
                        showMessage(data?.detail, "error");
                    }

                    setPopupOpen(false);
                }}
            />
        </Box>
    );
};

export default ItemMaster;